import React, { useState, useRef, useEffect } from 'react';
import { FiSend, FiPaperclip, FiMessageSquare, FiCreditCard, FiInfo, FiArrowLeft, FiUser, FiUsers, FiClock, FiMenu } from '../utils/icons';
import Layout from '../components/layout/Layout';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import mascot from '../assets/placeholder.png';
import axios from 'axios';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot' | 'expert';
  timestamp: Date;
  expertName?: string;
  options?: string[];
  isUser: boolean;
  typing?: boolean;
}

interface ChatHistory {
  id: string;
  title: string;
  messages: Message[];
  timestamp: Date;
  expertMode: boolean;
}

interface ChatQuery {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
}

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [chatStarted, setChatStarted] = useState(false);
  const [expertMode, setExpertMode] = useState(false);
  const [expertJoined, setExpertJoined] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [activeChatName, setActiveChatName] = useState('Matrix Finance Assistant');
  const [isConnecting, setIsConnecting] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatHistory[]>([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [isApiLoading, setIsApiLoading] = useState(false);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const scrollContainer = messagesContainerRef.current;
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  const commonQueries: ChatQuery[] = [
    {
      id: 'financial',
      title: 'Financial Queries',
      icon: <FiMessageSquare className="text-primary" size={24} />,
      description: 'Get quick answers to common financial concerns without entering the chat.',
    },
    {
      id: 'account',
      title: 'Account & Login Issues',
      icon: <FiInfo className="text-secondary" size={24} />,
      description: 'Having trouble signing in?',
    },
    {
      id: 'payment',
      title: 'Payment & Subscription Issues',
      icon: <FiCreditCard className="text-accent" size={24} />,
      description: 'Facing billing problems, unexpected charges, or need a refund?',
    },
    {
      id: 'guidance',
      title: 'General App Guidance',
      icon: <FiInfo className="text-success" size={24} />,
      description: 'Not sure how to use a feature?',
    },
  ];

  const suggestedQuestions = [
    'Tell me about budgeting.',
    'How can I start investing?',
    'What\'s the best way to save money?',
    'Explain credit scores to me.',
    'How do I manage debt effectively?',
    'What are the ways to build wealth?',
  ];

  const budgetingResponses = [
    {
      text: 'Budgeting is the key to financial success! A simple method to follow is the 50/30/20 rule:\n\n50% for Needs (Rent, Bills, Groceries)\n30% for Wants (Entertainment, Dining Out)\n20% for Savings & Investments\n\nWould you like a step-by-step guide to creating your personal budget?',
      options: [
        'Give me a step-by-step guide.',
        'How do I save money while budgeting?',
        'Not satisfied? Connect me with an expert.',
        'Go back to Main Menu',
      ],
    },
  ];

  const handleSend = async () => {
    if (input.trim() === '') return;

    // Add user message
    const newUserMessage: Message = {
      id: Date.now().toString(),
      text: input,
      sender: 'user',
      timestamp: new Date(),
      isUser: true,
    };
    setMessages([...messages, newUserMessage]);
    setInput('');
    
    const userMessageContent = input;

    // If this is the first message and it's "hi", start the conversation
    if (!chatStarted && input.toLowerCase() === 'hi') {
      setChatStarted(true);
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Hello, I\'m fine, how can I help you?',
          sender: 'bot',
          timestamp: new Date(),
          options: suggestedQuestions,
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
      return;
    }

    // Handle budgeting question
    if (input.toLowerCase().includes('budgeting')) {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: budgetingResponses[0].text,
          sender: 'bot',
          timestamp: new Date(),
          options: budgetingResponses[0].options,
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
      return;
    }

    // Handle expert connection request
    if (input.toLowerCase().includes('expert') || input.toLowerCase().includes('connect me')) {
      setExpertMode(true);
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Connecting you with an expert...\nEstimated wait time: 5 mins. In the meantime, describe your issue and attach any relevant documents.',
          sender: 'bot',
          timestamp: new Date(),
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);

      // Simulate expert joining after a delay
      setTimeout(() => {
        setExpertJoined(true);
        const expertResponse: Message = {
          id: (Date.now() + 2).toString(),
          text: 'Hi there! I\'m Aadi, your financial advisor. I understand managing expenses can be tricky. Could you share a bit more about your monthly income and spending habits?',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 5000);
      return;
    }

    // Handle expert conversation
    if (expertMode && expertJoined) {
      setTimeout(() => {
        let expertResponse: Message;
        
        if (input.toLowerCase().includes('thank you') || input.toLowerCase().includes('thanks')) {
          expertResponse = {
            id: (Date.now() + 1).toString(),
            text: 'You\'re very welcome! If you ever need more financial guidance, feel free to reach out. Wishing you success in your budgeting journey.',
            sender: 'expert',
            timestamp: new Date(),
            expertName: 'Aadi',
            isUser: false,
          };
          
          // Add closing message
          setTimeout(() => {
            const closingMessage: Message = {
              id: (Date.now() + 2).toString(),
              text: 'I am closing this chat!!',
              sender: 'expert',
              timestamp: new Date(),
              expertName: 'Aadi',
              isUser: false,
            };
            
            const endMessage: Message = {
              id: (Date.now() + 3).toString(),
              text: 'Chat Ended',
              sender: 'bot',
              timestamp: new Date(),
              isUser: false,
            };
            
            setMessages(prevMessages => [...prevMessages, closingMessage, endMessage]);
            
            // Reset chat after a delay
            setTimeout(() => {
              saveCurrentChatToHistory();
              setExpertMode(false);
              setExpertJoined(false);
              setChatStarted(false);
              setMessages([]);
            }, 3000);
          }, 2000);
        } else if (input.toLowerCase().includes('income') || input.toLowerCase().includes('$4,000') || input.toLowerCase().includes('4000')) {
          expertResponse = {
            id: (Date.now() + 1).toString(),
            text: 'Got it! A good approach is the 50/30/20 rule:\n50% for Needs (Rent, bills, groceries)\n30% for Wants (Dining, shopping, entertainment)\n20% for Savings & Investments\n\nWould you like help setting up a budget plan based on this?',
            sender: 'expert',
            timestamp: new Date(),
            expertName: 'Aadi',
            options: ['Yes, please help me set up a budget', 'No, I think I have everything I need. Thank you for your help!'],
            isUser: false,
          };
        } else {
          expertResponse = {
            id: (Date.now() + 1).toString(),
            text: 'That\'s helpful information. Based on what you\'ve shared, I\'d recommend tracking your expenses for a month using a budgeting app or spreadsheet. This will give you clear visibility into where your money is going. Shall we discuss some specific budgeting strategies that might work for your situation?',
            sender: 'expert',
            timestamp: new Date(),
            expertName: 'Aadi',
            isUser: false,
          };
        }
        
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 2000);
      return;
    }

    // Default bot response for other inputs when chat has started - Use API for this case
    if (chatStarted) {
      setIsTyping(true);
      setIsApiLoading(true);
      
      try {
        // First API call to get the main content
        const contentResponse = await axios.post(
          'https://ddtgdhehxhgarkonvpfq.supabase.co/functions/v1/createContent',
          {
            prompt: userMessageContent + (fileContent ? `\n\n[Image data: ${fileContent}]` : '')
          },
          {
            headers: {
              'Content-Type': 'application/json'
            },
            timeout: 30000 // 30 second timeout
          }
        );
        
        // Extract AI response
        const aiContent = contentResponse.data.output.text;
        
        // Add the bot message with typing animation
        const botResponseId = Date.now().toString();
        const initialBotResponse: Message = {
          id: botResponseId,
          text: '',
          sender: 'bot',
          timestamp: new Date(),
          isUser: false,
          typing: true
        };
        
        setMessages(prevMessages => [...prevMessages, initialBotResponse]);
        
        // Simulate typing animation with a more natural feel
        await simulateNaturalTyping(botResponseId, aiContent);
        
        // When typing is complete, get the options from the second API call
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            msg.id === botResponseId 
              ? { ...msg, typing: false } 
              : msg
          )
        );
        
        // Second API call to generate options
        generateOptionsForResponse(botResponseId, aiContent, userMessageContent);
        
        // Clear file content after it's been used
        setFileContent(null);
        
      } catch (error) {
        console.error('Error fetching AI response:', error);
        
        let errorMessage = 'I understand you\'re asking about ' + input.toLowerCase() + '. Would you like me to connect you with a financial expert who can provide more detailed guidance on this topic?';
        
        // More specific error handling
        if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
          errorMessage = 'I\'m sorry, but my response is taking longer than expected. Would you like to try a different question or connect with an expert?';
        } else if (axios.isAxiosError(error) && error.response) {
          errorMessage = 'I\'m experiencing some technical difficulties right now. Would you like to try again or speak with a financial expert?';
        }
        
        // Fallback response in case of API failure
        const fallbackResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: errorMessage,
          sender: 'bot',
          timestamp: new Date(),
          options: ['Yes, connect me with an expert', 'No, I have another question'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, fallbackResponse]);
      } finally {
        setIsTyping(false);
        setIsApiLoading(false);
      }
    }
  };

  // Function to simulate natural typing with variable speed and pauses
  const simulateNaturalTyping = async (messageId: string, fullText: string) => {
    return new Promise<void>((resolve) => {
      let displayedText = '';
      const characters = fullText.split('');
      let index = 0;
      
      const typeNextCharacter = () => {
        if (index < characters.length) {
          const char = characters[index];
          displayedText += char;
          
          setMessages(prevMessages => 
            prevMessages.map(msg => 
              msg.id === messageId 
                ? { ...msg, text: displayedText } 
                : msg
            )
          );
          
          index++;
          
          // Add natural pauses and variable typing speed
          let delay = 10; // Base typing speed (milliseconds)
          
          // Slow down at punctuation
          if (['.', '!', '?', ',', ';', ':'].includes(char)) {
            delay = char === '.' || char === '!' || char === '?' ? 300 : 150;
          } 
          // Add variations to simulate human typing
          else {
            delay = Math.random() * 20 + 5; // Between 5-25ms
            
            // Occasionally add a longer pause to simulate thinking
            if (Math.random() < 0.01) { // 1% chance
              delay += 200;
            }
          }
          
          // Speed up for longer messages
          if (fullText.length > 200) {
            delay = delay * 0.7;
          }
          
          // New paragraph gets an extra pause
          if (char === '\n') {
            delay += 200;
          }
          
          setTimeout(typeNextCharacter, delay);
        } else {
          resolve();
        }
      };
      
      typeNextCharacter();
    });
  };

  // New function to generate options via API
  const generateOptionsForResponse = async (messageId: string, aiContent: string, userQuestion: string) => {
    try {
      // Make API call to generate options
      const optionsResponse = await axios.post(
        'https://ddtgdhehxhgarkonvpfq.supabase.co/functions/v1/createContent',
        {
          prompt: `Based on this conversation:\n\nUser: ${userQuestion}\n\nAI: ${aiContent}\n\nGenerate 4 follow-up question options that the user might want to ask next. Make them relevant to the topic discussed. Format the response as a JSON array of strings. For example: ["Option 1", "Option 2", "Option 3", "Option 4"]`
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 20000
        }
      );
      
      // Extract the options from the response
      let options: string[] = [];
      
      try {
        // Try to parse as JSON first
        const responseText = optionsResponse.data.output.text;
        
        // Handle various JSON formats that might be returned
        if (responseText.includes('[') && responseText.includes(']')) {
          const jsonStr = responseText.substring(
            responseText.indexOf('['),
            responseText.lastIndexOf(']') + 1
          );
          options = JSON.parse(jsonStr);
        } else {
          // If not valid JSON, extract as text
          options = responseText.split('\n')
            .filter((line: string) => line.trim().length > 0)
            .map((line: string) => line.replace(/^[0-9\-\*\•\.]+\s*/, '').trim()) // Remove numbers, bullets
            .slice(0, 4); // Take max 4 options
        }
      } catch (err) {
        console.error('Error parsing options:', err);
        // Fallback options generation based on content
        if (aiContent.toLowerCase().includes('invest')) {
          options = ['Tell me about stocks', 'Explain index funds', 'What are ETFs?', 'Connect me with a financial advisor'];
        } else if (aiContent.toLowerCase().includes('budget')) {
          options = ['How do I save money while budgeting?', 'Give me a step-by-step guide.', 'Not satisfied? Connect me with an expert.', 'Go back to Main Menu'];
        } else {
          options = ['Get detailed explanation', 'Show me examples', 'How can I apply this?', 'Connect with an expert'];
        }
      }
      
      // Always add the option to talk to an expert
      if (!options.some(opt => opt.toLowerCase().includes('expert'))) {
        options.push('Connect with an expert');
      }
      
      // Update the message with the options
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === messageId 
            ? { ...msg, options } 
            : msg
        )
      );
    } catch (error) {
      console.error('Error generating options:', error);
      // Fallback options if API fails
      const fallbackOptions = ['Tell me more', 'Show me examples', 'How can I apply this?', 'Connect with an expert'];
      
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg.id === messageId 
            ? { ...msg, options: fallbackOptions } 
            : msg
        )
      );
    }
  };

  const handleOptionClick = (option: string, messageId: string) => {
    // Add user message with the selected option
    const newUserMessage: Message = {
      id: Date.now().toString(),
      text: option,
      sender: 'user',
      timestamp: new Date(),
      isUser: true,
    };
    
    // Remove options from the clicked message
    const updatedMessages = messages.map(msg => {
      if (msg.id === messageId) {
        return { ...msg, options: undefined };
      }
      return msg;
    });
    
    setMessages([...updatedMessages, newUserMessage]);
    
    // Handle specific options
    if (option === 'Tell me about budgeting.') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: budgetingResponses[0].text,
          sender: 'bot',
          timestamp: new Date(),
          options: budgetingResponses[0].options,
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'How can I start investing?') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Investing is a great way to build wealth over time! Here's how to get started:\n\n1. Set clear financial goals\n2. Build an emergency fund first (3-6 months of expenses)\n3. Pay off high-interest debt\n4. Understand investment basics (stocks, bonds, ETFs)\n5. Start with retirement accounts like 401(k) or IRA\n6. Consider low-cost index funds for beginners\n7. Only invest money you won't need soon\n\nWould you like to learn more about a specific investment type?",
          sender: 'bot',
          timestamp: new Date(),
          options: ['Tell me about stocks', 'Explain index funds', 'What are ETFs?', 'Connect me with a financial advisor'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === "What's the best way to save money?") {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Here are proven strategies to save money effectively:\n\n1. Follow the 50/30/20 rule (50% needs, 30% wants, 20% savings)\n2. Automate your savings with direct deposit\n3. Cut unnecessary subscriptions\n4. Use cashback apps and credit cards (paying in full monthly)\n5. Plan meals and cook at home\n6. Wait 24-48 hours before making non-essential purchases\n7. Set specific savings goals\n8. Use high-yield savings accounts\n\nWould you like more specific advice on any of these strategies?",
          sender: 'bot',
          timestamp: new Date(),
          options: ['Tell me about high-yield savings', 'How to cut expenses', 'Automated saving tips', 'Speak with a savings specialist'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Explain credit scores to me.') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Your credit score is a number (typically 300-850) that represents your creditworthiness. Here's what affects it:\n\n• Payment history (35%): Always pay on time\n• Credit utilization (30%): Keep balances low\n• Credit history length (15%): Longer is better\n• Credit mix (10%): Different types of credit\n• New credit inquiries (10%): Avoid too many applications\n\nScores above 740 are generally considered excellent. What else would you like to know about credit?",
          sender: 'bot',
          timestamp: new Date(),
          options: ['How to improve my score', 'Get my free credit report', 'Credit score myths', 'Speak with a credit specialist'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'How do I manage debt effectively?') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Managing debt effectively is crucial for financial health! Here are proven strategies:\n\n1. List all debts with interest rates and minimum payments\n2. Choose a payoff strategy:\n   • Avalanche Method: Highest interest rate first (saves money)\n   • Snowball Method: Smallest balance first (builds momentum)\n3. Always pay minimums on all debts\n4. Put extra money toward your target debt\n5. Consider debt consolidation for high-interest debts\n6. Negotiate with creditors when possible\n\nWhich aspect would you like to explore further?",
          sender: 'bot',
          timestamp: new Date(),
          options: ['Avalanche vs Snowball method', 'Debt consolidation options', 'Negotiating with creditors', 'Get personalized debt advice'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'What are the ways to build wealth?') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Building wealth is a long-term journey! Here are proven strategies:\n\n1. Consistent investing in diverse assets\n2. Maximize retirement accounts (401k, IRA)\n3. Build multiple income streams\n4. Develop valuable skills to increase earning potential\n5. Start a business or side hustle\n6. Invest in real estate (property, REITs)\n7. Avoid lifestyle inflation as income grows\n8. Practice tax-efficient investing\n\nWhich wealth-building strategy interests you most?",
          sender: 'bot',
          timestamp: new Date(),
          options: ['Investment strategies', 'Building income streams', 'Real estate investing', 'Talk to a wealth advisor'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Give me a step-by-step guide.') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Here's your step-by-step budgeting guide:\n\n1. Calculate your monthly income (after tax)\n2. Track all expenses for a month\n3. Categorize spending (needs, wants, savings)\n4. Set realistic spending limits for each category\n5. Use the 50/30/20 rule as a starting framework\n6. Choose a budgeting method (envelope, zero-based, etc.)\n7. Set up automatic bill payments and savings transfers\n8. Review and adjust your budget monthly\n9. Use apps like Mint, YNAB, or Excel spreadsheets to stay organized\n\nWould you like to discuss a specific step in more detail?",
          sender: 'bot',
          timestamp: new Date(),
          options: ['Budgeting apps recommendation', 'Envelope budgeting explained', 'Zero-based budgeting explained', 'Connect with a budget specialist'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'How do I save money while budgeting?') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Here are effective ways to save money while budgeting:\n\n1. Prioritize needs over wants\n2. Meal plan and cook at home\n3. Use cash envelopes for discretionary spending\n4. Implement a 24-hour rule for non-essential purchases\n5. Find free or low-cost entertainment\n6. Use cashback apps and credit card rewards\n7. Negotiate bills (internet, phone, insurance)\n8. Buy in bulk for non-perishable items\n9. Use the library for books, movies, and resources\n10. Automate savings transfers on payday\n\nWould you like more specific tips for a particular area?",
          sender: 'bot',
          timestamp: new Date(),
          options: ['Grocery saving tips', 'Lower my utility bills', 'Best cashback apps', 'Speak with a saving specialist'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Not satisfied? Connect me with an expert.') {
      setExpertMode(true);
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Connecting you with an expert...\nEstimated wait time: 5 mins. In the meantime, describe your issue and attach any relevant documents.',
          sender: 'bot',
          timestamp: new Date(),
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);

      // Simulate expert joining after a delay
      setTimeout(() => {
        setExpertJoined(true);
        const expertJoinedMessage: Message = {
          id: (Date.now() + 2).toString(),
          text: 'Expert Aadi joined',
          sender: 'bot',
          timestamp: new Date(),
          isUser: false,
        };
        
        const expertResponse: Message = {
          id: (Date.now() + 3).toString(),
          text: 'Hi there! I\'m Aadi, your financial advisor. I understand managing expenses can be tricky. Could you share a bit more about your monthly income and spending habits?',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          isUser: false,
        };
        
        setMessages(prevMessages => [...prevMessages, expertJoinedMessage, expertResponse]);
      }, 5000);
    } else if (option === 'Go back to Main Menu') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'How else can I help you today?',
          sender: 'bot',
          timestamp: new Date(),
          options: suggestedQuestions,
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Yes, connect me with an expert') {
      setExpertMode(true);
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Connecting you with an expert...\nEstimated wait time: 5 mins. In the meantime, describe your issue and attach any relevant documents.',
          sender: 'bot',
          timestamp: new Date(),
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);

      // Simulate expert joining after a delay
      setTimeout(() => {
        setExpertJoined(true);
        const expertJoinedMessage: Message = {
          id: (Date.now() + 2).toString(),
          text: 'Expert Aadi joined',
          sender: 'bot',
          timestamp: new Date(),
          isUser: false,
        };
        
        const expertResponse: Message = {
          id: (Date.now() + 3).toString(),
          text: 'Hi there! I\'m Aadi, your financial advisor. I\'m here to help with personalized guidance. Could you share what specific financial topics you need assistance with?',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          isUser: false,
        };
        
        setMessages(prevMessages => [...prevMessages, expertJoinedMessage, expertResponse]);
      }, 5000);
    } else if (option === 'No, I have another question') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'I\'m happy to help with your other questions! What would you like to know about?',
          sender: 'bot',
          timestamp: new Date(),
          options: suggestedQuestions,
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Yes, please help me set up a budget') {
      setTimeout(() => {
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Great! Let\'s create a personalized budget plan based on the 50/30/20 rule. For someone with a $4,000 monthly income, that would be:\n\n• $2,000 for Needs (housing, utilities, groceries, etc.)\n• $1,200 for Wants (dining out, entertainment, shopping)\n• $800 for Savings & Debt Repayment\n\nHow does this allocation look for your situation? We can adjust based on your specific circumstances.',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          options: ['This works for me', 'I need to spend more on needs', 'I want to save more than 20%'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 1500);
    } else if (option === 'No, I think I have everything I need. Thank you for your help!') {
      setTimeout(() => {
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'You\'re very welcome! If you ever need more financial guidance, feel free to reach out. Wishing you success in your budgeting journey.',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          isUser: false,
        };
        
        setMessages(prevMessages => [...prevMessages, expertResponse]);
        
        // Add closing message
        setTimeout(() => {
          const closingMessage: Message = {
            id: (Date.now() + 2).toString(),
            text: 'I am closing this chat!!',
            sender: 'expert',
            timestamp: new Date(),
            expertName: 'Aadi',
            isUser: false,
          };
          
          const endMessage: Message = {
            id: (Date.now() + 3).toString(),
            text: 'Chat Ended',
            sender: 'bot',
            timestamp: new Date(),
            isUser: false,
          };
          
          setMessages(prevMessages => [...prevMessages, closingMessage, endMessage]);
          
          // Reset chat after a delay
          setTimeout(() => {
            saveCurrentChatToHistory();
            setExpertMode(false);
            setExpertJoined(false);
            setChatStarted(false);
            setMessages([]);
          }, 3000);
        }, 2000);
      }, 1000);
    } else if (option === 'Provide basic information') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Here\'s some basic information about this topic. Financial literacy is key to making informed decisions about your money. Would you like to delve deeper into any specific aspect?',
          sender: 'bot',
          timestamp: new Date(),
          options: ['Learn about budgeting', 'Investment basics', 'Debt management', 'Speak with an expert'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Tell me about stocks') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Stocks represent ownership in a company. When you buy a stock, you're purchasing a small piece of that business.\n\nKey stock concepts:\n• Growth stocks: Companies expected to grow faster than average\n• Value stocks: Companies trading below their intrinsic value\n• Dividends: Payments companies make to shareholders\n• Market cap: Total value of a company's outstanding shares\n\nFor beginners, it's often best to start with diverse index funds rather than picking individual stocks.\n\nWould you like more specific information?",
          sender: 'bot',
          timestamp: new Date(),
          options: ['How to buy stocks', 'Stock market risks', 'Blue-chip stocks', 'Speak with an investment advisor'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Explain index funds') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Index funds are a type of mutual fund or ETF that tracks a specific market index, like the S&P 500.\n\nBenefits of index funds:\n• Low fees compared to actively managed funds\n• Built-in diversification across many stocks\n• Historically outperform most actively managed funds\n• Passive management requires less research\n• Tax-efficient investing option\n\nPopular index funds track the S&P 500, total US stock market, international markets, and bond indices.\n\nWhat else would you like to know about index investing?",
          sender: 'bot',
          timestamp: new Date(),
          options: ['Best index funds for beginners', 'Index vs. active funds', 'How to invest in index funds', 'Talk to an investment expert'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Get detailed explanation') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Here's a detailed explanation of this financial topic. Understanding the fundamentals can help you make better financial decisions. Would you like to know about specific strategies or practical applications?",
          sender: 'bot',
          timestamp: new Date(),
          options: ['Show me strategies', 'Practical applications', 'Related topics', 'Connect with an expert'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Show me examples') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Here are some real-world examples that illustrate this financial concept in action. These examples should help you understand how to apply these principles to your own financial situation.",
          sender: 'bot',
          timestamp: new Date(),
          options: ['Get more examples', 'How to implement this', 'Related concepts', 'Talk to an expert'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'How can I apply this?') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "Here are practical steps to apply this financial concept to your life:\n\n1. Assess your current financial situation\n2. Set clear, measurable goals\n3. Create a step-by-step action plan\n4. Implement one change at a time\n5. Track your progress regularly\n6. Adjust your approach as needed\n\nWould you like guidance on any specific step?",
          sender: 'bot',
          timestamp: new Date(),
          options: ['Help setting goals', 'Creating an action plan', 'Tracking methods', 'Get personalized advice'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Connect with an expert' || option === 'Talk to an expert' || option === 'Get personalized advice' || option === 'Speak with an expert' || option === 'Talk to a wealth advisor' || option === 'Connect with a financial advisor' || option === 'Speak with an investment advisor' || option === 'Get personalized debt advice' || option === 'Speak with a credit specialist' || option === 'Speak with a saving specialist' || option === 'Connect with a budget specialist') {
      setExpertMode(true);
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Connecting you with a financial expert who specializes in this topic...\nEstimated wait time: 5 mins. Feel free to provide more details about your situation while waiting.',
          sender: 'bot',
          timestamp: new Date(),
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);

      // Simulate expert joining after a delay
      setTimeout(() => {
        setExpertJoined(true);
        const expertJoinedMessage: Message = {
          id: (Date.now() + 2).toString(),
          text: 'Expert Aadi joined',
          sender: 'bot',
          timestamp: new Date(),
          isUser: false,
        };
        
        const expertResponse: Message = {
          id: (Date.now() + 3).toString(),
          text: 'Hello! I\'m Aadi, your financial specialist. I understand you\'re looking for personalized guidance on this topic. To give you the most relevant advice, could you share a bit more about your specific situation?',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          isUser: false,
        };
        
        setMessages(prevMessages => [...prevMessages, expertJoinedMessage, expertResponse]);
      }, 5000);
    } else if (option === 'This works for me') {
      setTimeout(() => {
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Excellent! I\'ll help you implement this budget plan. First, let\'s break down your "Needs" category into specific amounts:\n\n• Housing (rent/mortgage): ~$1,000\n• Utilities: ~$300\n• Groceries: ~$400\n• Transportation: ~$200\n• Insurance: ~$100\n\nDoes this breakdown seem realistic for your situation? We can adjust any category as needed.',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          options: ['Yes, this looks good', 'I need to adjust some categories'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 1500);
    } else if (option === 'I need to spend more on needs' || option === 'I need to adjust some categories') {
      setTimeout(() => {
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'No problem! Let\'s customize your budget to better fit your situation. What specific categories need adjustment? For example, do you need more for housing, healthcare, transportation, or other essential expenses?',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 1500);
    } else if (option === 'I want to save more than 20%') {
      setTimeout(() => {
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'That\'s an excellent goal! Increasing your savings rate is one of the best ways to build wealth. To save more than 20%, we\'ll need to optimize your "Wants" category. Here\'s an adjusted plan for your $4,000 income:\n\n• Needs: $2,000 (50%)\n• Wants: $800 (20%)\n• Savings: $1,200 (30%)\n\nThis would accelerate your financial goals significantly. Does this balance seem achievable for you?',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          options: ['Yes, I can make this work', 'That\'s too aggressive for me'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 1500);
    } else if (option === 'Yes, I can make this work' || option === 'Yes, this looks good') {
      setTimeout(() => {
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Great! Now let\'s talk about implementation. I recommend these steps:\n\n1. Set up automatic transfers to your savings account on payday\n2. Use a budgeting app like Mint, YNAB, or Personal Capital to track spending\n3. Review your subscriptions and cancel any you don\'t use regularly\n4. Plan meals ahead to reduce food costs\n5. Consider using cash for discretionary spending\n\nWould you like me to guide you through setting up your tracking system?',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          options: ['Yes, help me set up tracking', 'No, I think I have everything I need. Thank you for your help!'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 1500);
    } else if (option === 'That\'s too aggressive for me') {
      setTimeout(() => {
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'I understand! Let\'s find a more comfortable balance. How about we start with a smaller adjustment:\n\n• Needs: $2,000 (50%)\n• Wants: $1,000 (25%)\n• Savings: $1,000 (25%)\n\nThis gives you a 5% increase in savings while keeping more flexibility for wants. Remember, even small increases in your savings rate can have significant long-term benefits.',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          options: ['This balance works better for me', 'I want to stay with the original 50/30/20'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 1500);
    } else if (option === 'Yes, help me set up tracking') {
      setTimeout(() => {
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'I recommend starting with a simple tracking system. Here are the steps:\n\n1. Choose a tool (I recommend Mint for beginners - it\'s free and connects to your accounts)\n2. Link your bank accounts and credit cards\n3. Categorize your transactions (most apps do this automatically)\n4. Set budget limits for each category\n5. Schedule a weekly 15-minute review of your spending\n6. Adjust as needed throughout the month\n\nWould you like more specific guidance on using any particular budgeting app?',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          options: ['Tell me more about Mint', 'What about YNAB?', 'I prefer spreadsheets', 'No, I think I have everything I need. Thank you for your help!'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 1500);
    } else if (option === 'Tell me more about Mint') {
      setTimeout(() => {
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Mint is a free budgeting app owned by Intuit that automatically syncs with your bank accounts and credit cards. Here are its main benefits:\n\n• Automatically categorizes transactions\n• Creates visual spending reports\n• Sends bill payment reminders\n• Tracks your credit score for free\n• Suggests ways to save money\n• Easy to use mobile app\n\nTo get started with Mint:\n1. Create an account at mint.com\n2. Link your financial accounts\n3. Review your spending categories\n4. Set up budget limits\n\nIs there anything specific about Mint you\'d like to know?',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          options: ['How secure is Mint?', 'Mint vs. other apps', 'No, I think I have everything I need. Thank you for your help!'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 1500);
    } else if (option === 'What about YNAB?') {
      setTimeout(() => {
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'You Need A Budget (YNAB) is a powerful budgeting tool with a different approach than Mint. Here\'s what makes it unique:\n\n• Uses a zero-based budgeting method ("give every dollar a job")\n• Focuses on proactive budgeting rather than tracking past spending\n• Has a strong educational component with free workshops\n• Encourages building a "buffer" to live on last month\'s income\n• Subscription-based ($84/year) but offers a free 34-day trial\n• Very responsive customer support\n\nYNAB typically appeals to people who want a more hands-on approach to budgeting. Would you like to know more about how to use it effectively?',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          options: ['YNAB four rules explained', 'Is it worth the cost?', 'No, I think I have everything I need. Thank you for your help!'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 1500);
    } else if (option === 'I prefer spreadsheets') {
      setTimeout(() => {
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Spreadsheets are an excellent choice for budget tracking! They offer complete customization and privacy. Here\'s how to create an effective budget spreadsheet:\n\n1. Set up these essential columns:\n   • Date, Category, Description, Amount, Payment Method\n\n2. Create tabs for:\n   • Monthly Budget\n   • Income Tracking\n   • Expense Categories\n   • Savings Goals\n\n3. Use formulas for automatic calculations (SUM, AVERAGE, etc.)\n\n4. Consider a template to start (Google Sheets and Excel have free budget templates)\n\nWould you like me to recommend some specific templates or formulas that could be helpful?',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          options: ['Recommend templates', 'Essential formulas to use', 'No, I think I have everything I need. Thank you for your help!'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 1500);
    } else if (option === 'How secure is Mint?' || option === 'Is it worth the cost?' || option === 'YNAB four rules explained' || option === 'Recommend templates' || option === 'Essential formulas to use') {
      setTimeout(() => {
        let responseText = '';
        
        if (option === 'How secure is Mint?') {
          responseText = 'Mint uses bank-level 256-bit encryption and security scanning with VeriSign. They use read-only access to your financial accounts (can\'t move money), multi-factor authentication, and security questions. Intuit (the parent company) has a strong record in financial security.\n\nFor extra security, I recommend:\n• Using a unique, strong password\n• Enabling two-factor authentication\n• Setting up email alerts for large transactions\n• Regularly monitoring your accounts\n\nMint\'s security is generally very good, but remember no online service is 100% risk-free.';
        } else if (option === 'Is it worth the cost?') {
          responseText = 'Whether YNAB is worth $84/year depends on your situation. Consider these points:\n\n• Many users report saving thousands in the first few months\n• The structured approach helps build lasting financial habits\n• The educational resources are valuable for beginners\n• Free alternatives exist but often with fewer features\n\nIf you\'re struggling with consistent overspending or want to save more aggressively, YNAB often pays for itself many times over. The free trial is a good way to test if it works for your style.';
        } else if (option === 'YNAB four rules explained') {
          responseText = 'YNAB\'s method centers around these four rules:\n\n1. Give Every Dollar a Job: Assign all income to specific categories before spending anything\n\n2. Embrace Your True Expenses: Plan for larger, less frequent expenses by setting aside money monthly\n\n3. Roll With The Punches: Be flexible and adjust your budget when circumstances change\n\n4. Age Your Money: Work toward spending money that\'s at least 30 days old (living on last month\'s income)\n\nThese principles help create a proactive rather than reactive approach to your finances, reducing stress and building financial stability.';
        } else if (option === 'Recommend templates') {
          responseText = 'Here are some excellent budget spreadsheet templates:\n\n1. Google Sheets: "Monthly Budget" template (pre-installed)\n\n2. Microsoft Excel: "Personal Monthly Budget" template\n\n3. Tiller Money: Automated templates that sync with your accounts (paid service)\n\n4. Vertex42.com: Free, detailed budget templates for specific needs (debt reduction, household budget, etc.)\n\nThe Google and Excel templates are good starting points for beginners. As you get more comfortable, you can customize them to better fit your specific needs.';
        } else if (option === 'Essential formulas to use') {
          responseText = 'These spreadsheet formulas will make budgeting easier:\n\n1. SUM: Total up expenses by category (=SUM(B2:B10))\n\n2. SUMIF: Sum only specific categories (=SUMIF(A2:A30,"Groceries",B2:B30))\n\n3. AVERAGE: Find your average spending (=AVERAGE(B2:B10))\n\n4. IF: Create conditional logic (=IF(B2>1000,"Over budget","OK"))\n\n5. VLOOKUP: Pull data from other sheets\n\n6. Conditional formatting: Highlight values that exceed budgets\n\nThese basic functions will handle most budgeting needs. Would you like me to explain any of these in more detail?';
        }
        
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: responseText,
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          options: ['This is helpful, thank you!', 'I have another question', 'No, I think I have everything I need. Thank you for your help!'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 1500);
    } else if (option === 'This is helpful, thank you!' || option === 'This balance works better for me') {
      setTimeout(() => {
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'I\'m glad this information is helpful! Establishing good financial habits is a journey, not a destination. Remember to review your plan regularly and adjust as your life circumstances change.\n\nIs there anything else I can help you with today regarding your finances?',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          options: ['I have another question', 'No, I think I have everything I need. Thank you for your help!'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 1500);
    } else if (option === 'I have another question') {
      setTimeout(() => {
        const expertResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Of course! I\'m happy to help with any other questions. What financial topic would you like to discuss next?',
          sender: 'expert',
          timestamp: new Date(),
          expertName: 'Aadi',
          options: ['Investing basics', 'Debt management', 'Saving strategies', 'Retirement planning', 'Insurance needs'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, expertResponse]);
      }, 1500);
    } else if (option === 'What are ETFs?') {
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: "ETFs (Exchange-Traded Funds) are investment funds traded on stock exchanges, similar to stocks. Here's what makes them popular:\n\n• Diversification: One ETF can contain hundreds of stocks/bonds\n• Low fees: Typically have lower expense ratios than mutual funds\n• Liquidity: Can be bought/sold throughout trading day at market prices\n• Accessibility: Available through any brokerage account\n• Tax efficiency: Generally create fewer taxable events than mutual funds\n\nPopular ETFs include those tracking the S&P 500 (VOO, SPY), total stock market (VTI), and bond market (BND).\n\nWould you like to know more about specific types of ETFs?",
          sender: 'bot',
          timestamp: new Date(),
          options: ['Types of ETFs explained', 'How to buy ETFs', 'ETF vs. mutual funds', 'Talk to an investment expert'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Types of ETFs explained' || option === 'How to buy ETFs' || option === 'ETF vs. mutual funds') {
      setTimeout(() => {
        let responseText = '';
        
        if (option === 'Types of ETFs explained') {
          responseText = "Here are the main types of ETFs available:\n\n• Index ETFs: Track specific market indices (S&P 500, NASDAQ, etc.)\n• Sector ETFs: Focus on specific industries (technology, healthcare, etc.)\n• Bond ETFs: Invest in various fixed-income securities\n• Commodity ETFs: Track prices of commodities like gold or oil\n• International ETFs: Invest in foreign markets\n• Dividend ETFs: Focus on dividend-paying stocks\n• Factor ETFs: Target specific investment factors (value, growth, momentum)\n• Inverse/Leveraged ETFs: Amplify market movements (more advanced/risky)\n\nFor most beginners, broad-based index ETFs are a great place to start.";
        } else if (option === 'How to buy ETFs') {
          responseText = "Buying ETFs is straightforward:\n\n1. Open a brokerage account (Fidelity, Vanguard, Charles Schwab, etc.)\n2. Fund your account from your bank\n3. Research ETFs that align with your goals (use screening tools)\n4. Enter the ETF's ticker symbol in the trade screen\n5. Specify the number of shares or dollar amount\n6. Choose order type (market order for immediate execution)\n7. Review and submit your order\n\nMany brokerages now offer commission-free ETF trading and fractional shares, making it easy to start with any amount of money.";
        } else if (option === 'ETF vs. mutual funds') {
          responseText = "Key differences between ETFs and mutual funds:\n\n• Trading: ETFs trade like stocks throughout the day; mutual funds trade once daily at market close\n• Minimum investment: ETFs have no minimums (beyond share price); mutual funds often require $1,000+ to start\n• Fees: ETFs typically have lower expense ratios\n• Tax efficiency: ETFs generally create fewer taxable events\n• Management: Most ETFs are passively managed; mutual funds can be active or passive\n• Transparency: ETFs disclose holdings daily; mutual funds disclose quarterly\n\nBoth are valid investment vehicles, but ETFs often have advantages for individual investors, especially beginners.";
        }
        
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: responseText,
          sender: 'bot',
          timestamp: new Date(),
          options: ['Tell me more about investing', 'Recommended ETFs for beginners', 'Investment risk management', 'Talk to an investment expert'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Tell me about high-yield savings' || option === 'How to cut expenses' || option === 'Automated saving tips') {
      setTimeout(() => {
        let responseText = '';
        
        if (option === 'Tell me about high-yield savings') {
          responseText = "High-yield savings accounts offer significantly better interest rates than traditional bank accounts:\n\n• Current rates: Often 10-15x higher than traditional savings accounts\n• FDIC/NCUA insured: Just as safe as regular bank accounts\n• Online-focused: Most top rates come from online banks with lower overhead\n• No/low fees: Many have no monthly maintenance fees\n• Easy access: Typically allow 6 withdrawals per month (federal regulation)\n\nPopular options include Ally Bank, Marcus by Goldman Sachs, Capital One 360, and SoFi. When comparing, look beyond the rate to fees, minimum balances, and how quickly you can access funds when needed.";
        } else if (option === 'How to cut expenses') {
          responseText = "Effective ways to reduce monthly expenses:\n\n1. Housing: Consider roommates, refinancing, or downsizing\n2. Transportation: Use public transit, carpool, or consider cheaper vehicle options\n3. Food: Meal plan, cook in bulk, use grocery apps/coupons, limit takeout\n4. Utilities: Adjust thermostat, use energy-efficient appliances, negotiate rates\n5. Subscriptions: Audit and cancel unused services, share accounts when possible\n6. Insurance: Shop around annually, bundle policies, adjust coverage\n7. Phone/Internet: Compare providers, negotiate, choose appropriate plans\n8. Shopping: Use 24-hour rule for non-essentials, buy used when possible\n\nStart by tracking all expenses for a month to identify your biggest opportunities for savings.";
        } else if (option === 'Automated saving tips') {
          responseText = "Automating your savings is the most effective way to build wealth consistently:\n\n1. Direct deposit splitting: Have your employer send a portion directly to savings\n2. Automatic transfers: Schedule regular transfers from checking to savings\n3. Round-up apps: Services like Acorns or Chime round up purchases and save the difference\n4. Save windfalls: Set up automatic rules for tax refunds, bonuses, and gifts\n5. Use multiple accounts: Create separate savings for different goals (emergency, vacation, etc.)\n6. Increase gradually: Boost your saving rate by 1% every few months\n7. Use bank rules: Some banks can automatically transfer funds above a certain checking balance\n\nThe key is making saving happen before you have a chance to spend the money.";
        }
        
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: responseText,
          sender: 'bot',
          timestamp: new Date(),
          options: ['Other saving strategies', 'Setting effective saving goals', 'Emergency fund tips', 'Speak with a savings specialist'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'How to improve my score' || option === 'Get my free credit report' || option === 'Credit score myths') {
      setTimeout(() => {
        let responseText = '';
        
        if (option === 'How to improve my score') {
          responseText = "Actionable steps to improve your credit score:\n\n1. Pay all bills on time (set up automatic payments)\n2. Reduce credit utilization below 30% (ideally under 10%)\n3. Don't close old credit cards (length of history matters)\n4. Limit applications for new credit\n5. Use a secured credit card if you have limited history\n6. Become an authorized user on someone's well-managed card\n7. Check your credit report for errors and dispute them\n8. Consider a credit-builder loan from a credit union\n\nBe patient—meaningful improvements typically take 3-6 months, and major negatives can take years to fully recover from.";
        } else if (option === 'Get my free credit report') {
          responseText = "You can get your free credit report in these ways:\n\n• AnnualCreditReport.com: The only officially authorized source for free annual reports from all three bureaus (Equifax, Experian, TransUnion)\n\n• Credit card providers: Many now offer free FICO score access\n\n• Credit monitoring services: Some offer free report access with registration\n\nDuring COVID-19, the bureaus are offering free weekly online reports (instead of annually).\n\nWhen reviewing your report, check for:\n- Incorrect personal information\n- Accounts you didn't open\n- Late payments you believe were on time\n- Incorrect account balances or credit limits";
        } else if (option === 'Credit score myths') {
          responseText = "Common credit score myths debunked:\n\n• Myth: Checking your own score lowers it\nFact: Your own checks are \"soft inquiries\" and don't affect your score\n\n• Myth: Carrying a balance improves your score\nFact: Paying in full is best; carrying a balance just costs you interest\n\n• Myth: Closing cards improves your score\nFact: This often hurts by reducing available credit and shortening history\n\n• Myth: Co-signing doesn't affect your credit\nFact: You're fully responsible for co-signed debt\n\n• Myth: All credit scores are the same\nFact: There are many scoring models; lenders use different ones\n\n• Myth: Higher income means better credit\nFact: Income isn't directly part of your credit score";
        }
        
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: responseText,
          sender: 'bot',
          timestamp: new Date(),
          options: ['Credit repair services: Worth it?', 'Credit score vs. credit report', 'Building credit from scratch', 'Speak with a credit specialist'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Avalanche vs Snowball method' || option === 'Debt consolidation options' || option === 'Negotiating with creditors') {
      setTimeout(() => {
        let responseText = '';
        
        if (option === 'Avalanche vs Snowball method') {
          responseText = "Comparing the two most popular debt payoff methods:\n\n• Debt Avalanche:\n  - Pay highest interest rate debt first\n  - Mathematically saves the most money\n  - Optimal from a financial perspective\n  - May take longer to eliminate individual debts\n\n• Debt Snowball:\n  - Pay smallest balance debt first\n  - Creates quick wins for psychological momentum\n  - Research shows higher completion rates\n  - May cost more in interest long-term\n\nBoth methods require making minimum payments on all debts while putting extra money toward your target debt. The best method is the one you'll stick with consistently.";
        } else if (option === 'Debt consolidation options') {
          responseText = "Ways to consolidate high-interest debt:\n\n1. Personal loan: Fixed term, typically lower rate than credit cards\n\n2. Balance transfer credit card: 0% intro rate (usually 12-18 months), typically with 3-5% transfer fee\n\n3. Home equity loan/HELOC: Lower rates but puts your home at risk\n\n4. 401(k) loan: Borrows from your retirement (risky, avoid if possible)\n\n5. Debt management plan: Nonprofit credit counselors negotiate rates and set up payment plan\n\n6. Cash-out refinance: Reset your mortgage to access equity\n\nConsolidation works best when it lowers your interest rate AND you avoid accumulating new debt while paying off the consolidated amount.";
        } else if (option === 'Negotiating with creditors') {
          responseText = "Effective strategies for negotiating with creditors:\n\n1. For current debts:\n• Call before missing payments\n• Ask about hardship programs\n• Request lower interest rates (especially with good payment history)\n• Inquire about fee waivers\n\n2. For past-due accounts:\n• Offer lump-sum settlements (typically 40-60% of balance)\n• Get all agreements in writing before paying\n• Understand tax implications of forgiven debt\n• Consider professional help for serious delinquencies\n\nThe earlier you communicate with creditors, the more options you'll have. Document all conversations with dates, representative names, and details of what was discussed.";
        }
        
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: responseText,
          sender: 'bot',
          timestamp: new Date(),
          options: ['Understanding debt-to-income ratio', 'Creating a debt payoff plan', 'Rebuilding after debt problems', 'Get personalized debt advice'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else if (option === 'Investment strategies' || option === 'Building income streams' || option === 'Real estate investing') {
      setTimeout(() => {
        let responseText = '';
        
        if (option === 'Investment strategies') {
          responseText = "Effective investment strategies for wealth building:\n\n1. Dollar-cost averaging: Invest a fixed amount regularly regardless of market conditions\n\n2. Asset allocation: Spread investments across stocks, bonds, real estate, etc. based on your risk tolerance and time horizon\n\n3. Diversification: Invest across different sectors, company sizes, and geographic regions\n\n4. Tax-advantaged investing: Maximize retirement accounts before taxable accounts\n\n5. Low-cost index funds: Minimize fees to maximize returns\n\n6. Value averaging: Adjust contribution amounts to keep growth on a steady trajectory\n\n7. Buy and hold: Focus on long-term growth rather than market timing\n\nThe most successful strategy is one you can stick with through market volatility.";
        } else if (option === 'Building income streams') {
          responseText = "Ways to develop multiple income streams:\n\n1. Passive income sources:\n• Dividend-paying stocks and funds\n• Real estate rentals or REITs\n• Bonds and fixed-income investments\n• Creating and selling digital products\n• Licensing intellectual property\n\n2. Active income sources:\n• Side business or freelancing\n• Part-time work in a different field\n• Consulting in your area of expertise\n• Teaching or tutoring\n• Monetizing hobbies (crafts, writing, etc.)\n\nStart small with one additional income stream before expanding. Even an extra $100-200 monthly can significantly impact your financial growth over time.";
        } else if (option === 'Real estate investing') {
          responseText = "Real estate investment approaches for different investors:\n\n1. Direct ownership options:\n• Residential rentals (single-family or multi-unit)\n• Fix-and-flip properties\n• Commercial real estate\n• Short-term rentals (Airbnb, VRBO)\n• House hacking (living in one unit while renting others)\n\n2. Indirect investment options:\n• Real Estate Investment Trusts (REITs)\n• Real estate mutual funds and ETFs\n• Real estate crowdfunding platforms\n• Limited partnerships\n\nReal estate can offer both income and appreciation, but requires research, capital, and often active management. For most people, starting with REITs is the easiest entry point.";
        }
        
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: responseText,
          sender: 'bot',
          timestamp: new Date(),
          options: ['Retirement investment planning', 'Managing investment risk', 'Starting with limited capital', 'Talk to a wealth advisor'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    } else {
      // Default handling for other options
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: `I see you're interested in "${option}". This is an important topic. Would you like me to provide some basic information, or would you prefer to speak with a financial expert for more personalized advice?`,
          sender: 'bot',
          timestamp: new Date(),
          options: ['Provide basic information', 'Connect me with an expert'],
          isUser: false,
        };
        setMessages(prevMessages => [...prevMessages, botResponse]);
      }, 1000);
    }
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const resetCategory = () => {
    setSelectedCategory(null);
  };

  const formatTimestamp = (timestamp: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(timestamp);
  };

  // Chat header rendering
  const renderChatHeader = () => {
    return (
      <div className="p-4 border-b border-border flex items-center">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary via-secondary to-accent p-0.5 mr-3">
          <div className="w-full h-full rounded-full bg-background flex items-center justify-center">
            <img src={mascot} alt="Matrix AI Assistant" className="w-7 h-7 object-contain" />
          </div>
        </div>
        <div>
          <h2 className="font-semibold">{activeChatName}</h2>
          <p className="text-xs text-text/70">AI-powered financial assistant</p>
        </div>
      </div>
    );
  };

  // Message avatar rendering
  const renderMessageAvatar = (message: Message) => {
    if (message.isUser) {
      return (
        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
          <FiUser className="text-primary" />
        </div>
      );
    } else if (message.expertName) {
      return (
        <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center">
          <FiUsers className="text-secondary" />
        </div>
      );
    } else {
      return (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary via-secondary to-accent p-0.5">
          <div className="w-full h-full rounded-full bg-background flex items-center justify-center">
            <img src={mascot} alt="Matrix AI Assistant" className="w-5 h-5 object-contain" />
          </div>
        </div>
      );
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSend();
  };

  // Save current chat to history when ending a chat
  const saveCurrentChatToHistory = () => {
    if (messages.length > 0) {
      const newChatHistory: ChatHistory = {
        id: Date.now().toString(),
        title: messages[0].text.length > 30 
          ? messages[0].text.substring(0, 30) + '...' 
          : messages[0].text,
        messages: [...messages],
        timestamp: new Date(),
        expertMode: expertMode
      };
      
      setChatHistory(prev => [newChatHistory, ...prev]);
    }
  };

  // Load a chat from history
  const loadChatFromHistory = (chatId: string) => {
    const selectedChat = chatHistory.find(chat => chat.id === chatId);
    if (selectedChat) {
      setMessages(selectedChat.messages);
      setExpertMode(selectedChat.expertMode);
      setExpertJoined(selectedChat.expertMode);
      setChatStarted(true);
      setIsHistoryOpen(false);
    }
  };

  // Start a new chat
  const startNewChat = () => {
    // Save current chat to history if any messages exist
    if (messages.length > 0) {
      saveCurrentChatToHistory();
    }
    
    // Reset the chat state
    setMessages([]);
    setExpertMode(false);
    setExpertJoined(false);
    setChatStarted(true);
    setIsHistoryOpen(false);
    setFileContent(null);
  };

  // Handle file upload
  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Add a message about the file being attached
      const newUserMessage: Message = {
        id: Date.now().toString(),
        text: `Attached file: ${file.name}`,
        sender: 'user',
        timestamp: new Date(),
        isUser: true,
      };
      setMessages([...messages, newUserMessage]);
      
      // Convert file to base64 for API
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64data = reader.result as string;
        setFileContent(base64data.split(',')[1]); // Remove the data:image/jpeg;base64, part
      };
      
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setShowFileUpload(false);
    }
  };

  return (
    <Layout hideFooter>
      <div className="flex flex-col h-screen bg-background">
        {/* Chat interface */}
        <div className="flex-grow">
          <div className="container mx-auto px-4 py-8 ">
            {!chatStarted ? (
              // Chat welcome screen
              <div className="max-w-2xl mx-auto">
                <Card variant="glass" className="p-6">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary via-secondary to-accent p-1 mb-4">
                      <div className="w-full h-full rounded-full bg-card flex items-center justify-center">
                        <img src={mascot} alt="Matrix Finance AI" className="w-12 h-12 object-contain" />
                      </div>
                    </div>
                    <h1 className="text-2xl font-bold mb-2">Chat with Matrix Finance AI</h1>
                    <p className="text-text/70">Get personalized financial advice and support from our AI assistant</p>
                  </div>
                  
                  <div className="flex justify-end mb-4">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => setIsHistoryOpen(!isHistoryOpen)}
                      className="text-text/70"
                    >
                      <FiClock size={16} className="mr-2" /> Chat History
                    </Button>
                  </div>

                  {/* Chat history panel for welcome screen */}
                  {isHistoryOpen && !chatStarted && (
                    <div className="border border-border rounded-lg mb-6 bg-card/50 overflow-hidden">
                      <div className="p-3 border-b border-border flex justify-between items-center bg-card/80">
                        <h3 className="font-semibold">Chat History</h3>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => setIsHistoryOpen(false)}
                        >
                          <FiArrowLeft size={16} />
                        </Button>
                      </div>
                      
                      <div className="p-4 max-h-[300px] overflow-y-auto">
                        {chatHistory.length === 0 ? (
                          <p className="text-sm text-text/50 text-center p-4">No chat history yet</p>
                        ) : (
                          <div className="space-y-2">
                            {chatHistory.map(chat => (
                              <div 
                                key={chat.id}
                                className="p-3 rounded-lg hover:bg-card-hover cursor-pointer border border-border"
                                onClick={() => loadChatFromHistory(chat.id)}
                              >
                                <div className="flex justify-between items-start">
                                  <h4 className="font-medium text-sm line-clamp-1">{chat.title}</h4>
                                  <span className="text-xs text-text/50">
                                    {new Date(chat.timestamp).toLocaleDateString()}
                                  </span>
                                </div>
                                <p className="text-xs text-text/70 mt-1">
                                  {chat.messages.length} messages
                                  {chat.expertMode && " • Expert chat"}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="mt-4">
                          <Button 
                            className="w-full" 
                            onClick={startNewChat}
                            disabled={chatHistory.length === 0}
                          >
                            Start New Chat
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Only show queries when history is not open */}
                  {!isHistoryOpen && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                      {commonQueries.map((query) => (
                        <Card key={query.id} className="p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => {
                          setChatStarted(true);
                          
                          // Add user message
                          const newUserMessage: Message = {
                            id: Date.now().toString(),
                            text: `Tell me about ${query.title.toLowerCase()}`,
                            sender: 'user',
                            timestamp: new Date(),
                            isUser: true,
                          };
                          
                          setMessages([newUserMessage]);
                          
                          // Add bot response
                          setTimeout(() => {
                            const botResponse: Message = {
                              id: (Date.now() + 1).toString(),
                              text: `Here's some information about ${query.title.toLowerCase()}. What specific aspect would you like to know more about?`,
                              sender: 'bot',
                              timestamp: new Date(),
                              options: ['Get detailed explanation', 'Show me examples', 'How can I apply this?', 'Connect with an expert'],
                              isUser: false,
                            };
                            setMessages(prevMessages => [...prevMessages, botResponse]);
                          }, 1000);
                        }}>
                          <div className="flex items-start">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                              {query.icon}
                            </div>
                            <div>
                              <h3 className="font-medium">{query.title}</h3>
                              <p className="text-sm text-text/70">{query.description}</p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex flex-col items-center">
                    <p className="text-text/70 mb-4">Type a message to start a conversation</p>
                    <div className="w-full flex items-center gap-2">
                      <input
                        type="text"
                        className="flex-grow rounded-lg border border-border bg-card px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Type your question here..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                      />
                      <Button onClick={handleSend}>
                        <FiSend size={18} />
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            ) : (
              // Active chat interface
              <div className="max-w-4xl mx-auto">
                <Card variant="glass" className="p-4 md:p-6 h-[calc(100vh-120px)]">
                  {/* Chat header */}
                  <div className="flex items-center justify-between pb-4 border-b border-border">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary via-secondary to-accent p-0.5">
                        <div className="w-full h-full rounded-full bg-card flex items-center justify-center">
                          <img src={mascot} alt="Matrix Finance AI" className="w-6 h-6 object-contain" />
                        </div>
                      </div>
                      <div className="ml-3">
                        <h2 className="font-semibold">
                          {expertMode ? (expertJoined ? 'Expert Advisor' : 'Connecting to Expert...') : 'Matrix Finance AI'}
                        </h2>
                        <p className="text-xs text-text/50">
                          {expertMode ? (expertJoined ? 'Live support' : 'Waiting for connection...') : 'AI-powered assistant'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => setIsHistoryOpen(!isHistoryOpen)}
                        className="text-text/70"
                      >
                        <FiClock size={16} className="mr-1" /> History
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => setChatStarted(false)}
                        className="text-text/70"
                      >
                        <FiArrowLeft size={16} className="mr-1" /> Back
                      </Button>
                    </div>
                  </div>
                  
                  {/* Chat history sidebar */}
                  {isHistoryOpen && (
                    <div className="absolute right-0 top-0 w-80 h-full bg-card border-l border-border shadow-lg z-10 overflow-y-auto">
                      <div className="p-4 border-b border-border flex justify-between items-center">
                        <h3 className="font-semibold">Chat History</h3>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => setIsHistoryOpen(false)}
                        >
                          <FiArrowLeft size={16} />
                        </Button>
                      </div>
                      
                      <div className="p-2">
                        <Button 
                          className="w-full mb-3" 
                          onClick={startNewChat}
                        >
                          Start New Chat
                        </Button>
                        
                        {chatHistory.length === 0 ? (
                          <p className="text-sm text-text/50 text-center p-4">No chat history yet</p>
                        ) : (
                          <div className="space-y-2">
                            {chatHistory.map(chat => (
                              <div 
                                key={chat.id}
                                className="p-3 rounded-lg hover:bg-card-hover cursor-pointer border border-border"
                                onClick={() => loadChatFromHistory(chat.id)}
                              >
                                <div className="flex justify-between items-start">
                                  <h4 className="font-medium text-sm line-clamp-1">{chat.title}</h4>
                                  <span className="text-xs text-text/50">
                                    {new Date(chat.timestamp).toLocaleDateString()}
                                  </span>
                                </div>
                                <p className="text-xs text-text/70 mt-1">
                                  {chat.messages.length} messages
                                  {chat.expertMode && " • Expert chat"}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Messages container */}
                  <div 
                    ref={messagesContainerRef}
                    className="flex-grow overflow-y-auto h-[calc(100%-140px)] py-4 space-y-4"
                  >
                    {messages.map((message, index) => (
                      <div key={message.id} className="flex flex-col">
                        <div className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] ${message.isUser 
                            ? 'bg-primary/10 text-text rounded-tl-xl rounded-tr-xl rounded-bl-xl' 
                            : 'bg-card rounded-tl-xl rounded-tr-xl rounded-br-xl shadow-sm'} p-4`}
                          >
                            {!message.isUser && message.sender === 'expert' && (
                              <div className="flex items-center mb-2">
                                <div className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center mr-2">
                                  <FiUser size={12} className="text-secondary" />
                                </div>
                                <span className="text-sm font-medium text-secondary">{message.expertName || 'Expert'}</span>
                              </div>
                            )}
                            
                            <p className="whitespace-pre-wrap">{message.text}</p>
                            
                            {/* Typing animation bubbles */}
                            {message.typing && (
                              <div className="flex items-center space-x-1 mt-2">
                                <div className="w-2 h-2 rounded-full bg-primary/50 animate-pulse"></div>
                                <div className="w-2 h-2 rounded-full bg-primary/50 animate-pulse delay-150"></div>
                                <div className="w-2 h-2 rounded-full bg-primary/50 animate-pulse delay-300"></div>
                              </div>
                            )}
                            
                            {message.options && (
                              <div className="mt-4 flex flex-wrap gap-2">
                                {message.options.map((option, i) => (
                                  <button
                                    key={i}
                                    className="px-3 py-1.5 text-sm rounded-full bg-card/60 border border-border hover:bg-card-hover"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleOptionClick(option, message.id);
                                    }}
                                  >
                                    {option}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Timestamp in center */}
                        <div className="flex justify-center my-2">
                          <span className="text-xs text-text/40 bg-background/60 px-2 py-0.5 rounded-full">
                            {formatTimestamp(message.timestamp)}
                          </span>
                        </div>
                      </div>
                    ))}
                    
                    {isTyping && !messages.some(msg => msg.typing) && (
                      <div className="flex justify-start">
                        <div className="bg-card rounded-tl-xl rounded-tr-xl rounded-br-xl p-4 max-w-[80%] shadow-sm">
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 rounded-full bg-primary/70 animate-pulse"></div>
                            <div className="w-2 h-2 rounded-full bg-primary/70 animate-pulse delay-75"></div>
                            <div className="w-2 h-2 rounded-full bg-primary/70 animate-pulse delay-150"></div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>
                  
                  {/* File upload panel */}
                  {showFileUpload && (
                    <div className="p-3 border-t border-border bg-card/50">
                      <div className="flex justify-between items-center">
                        <p className="text-sm">Attach a file</p>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setShowFileUpload(false)}
                        >
                          <FiArrowLeft size={14} />
                        </Button>
                      </div>
                      <div 
                        className="mt-2 border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:bg-card"
                        onClick={handleFileUpload}
                      >
                        <p className="text-sm">Click to select a file or drag and drop</p>
                        <p className="text-xs text-text/50 mt-1">Supported formats: PDF, JPG, PNG, DOC(X)</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Input area */}
                  <div className="pt-4 border-t border-border">
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      />
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-text/50"
                        aria-label="Attach file"
                        onClick={() => setShowFileUpload(!showFileUpload)}
                        disabled={isApiLoading}
                      >
                        <FiPaperclip size={18} />
                      </Button>
                      <input
                        type="text"
                        className="flex-grow rounded-lg border border-border bg-card px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder={expertMode 
                          ? 'Type your message to the expert...' 
                          : 'Ask anything about your finances...'
                        }
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                        disabled={isApiLoading}
                      />
                      <Button onClick={handleSend} disabled={isApiLoading || input.trim() === ''}>
                        {isApiLoading ? (
                          <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                        ) : (
                          <FiSend size={18} />
                        )}
                      </Button>
                    </div>
                    {fileContent && (
                      <div className="mt-2 p-2 bg-primary/10 rounded-md flex justify-between items-center">
                        <span className="text-xs">File attached</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setFileContent(null)}
                          className="text-text/70 hover:text-text"
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Chat; 